import type Decimal from 'decimal.js';
import { upperFirst } from 'lodash-es';

export const convertEnumToString = (string_?: string, capAll = true): string | undefined => {
  if (!string_) {
    return string_;
  }
  const lowered = string_.split('_').join(' ').toLowerCase();

  return capAll
    ? lowered
        .split(/\s/g)
        .map(word => upperFirst(word))
        .join(' ')
    : upperFirst(lowered);
};

export const convertedNestedString = (string_: string): string => {
  if (!string_) {
    return string_;
  }

  return string_.split('.').join('_').replaceAll(/[[\]]/g, '');
};

export const truncateBetween = (string_: string, fromIndex: number, toIndex: number): string => {
  if (string_.length < fromIndex + toIndex) {
    return string_;
  }

  return string_.slice(0, Math.max(0, fromIndex)) + '...' + string_.slice(string_.length - toIndex);
};

const ELLIPSIS = '...';

export const truncateMiddleToLength = (string_: string, maxLength: number): string => {
  if (maxLength < ELLIPSIS.length) {
    throw new Error(
      `truncateMiddleToLength: The ellipsis itself is ${ELLIPSIS.length} characters long. Please pass in a length greater than that.`
    );
  }

  const base = string_.trim();
  const stringLength = base.length;

  if (stringLength > maxLength) {
    const delta = (stringLength - maxLength) / 2 + ELLIPSIS.length / 2;
    const halfwayPoint = stringLength / 2;

    const fromIndex = Math.ceil(halfwayPoint - delta);
    const toIndex = Math.ceil(halfwayPoint + delta);

    const firstHalf = base.slice(0, Math.max(0, fromIndex));
    const latterHalf = base.slice(Math.max(0, toIndex));

    const trimmedFirstHalf = firstHalf.trim();

    if (firstHalf.length > trimmedFirstHalf.length) {
      return `${trimmedFirstHalf}${ELLIPSIS}${base.slice(Math.max(0, toIndex - 1)).trim()}`;
    }

    const trimmedLatterHalf = latterHalf.trim();

    if (latterHalf.length > trimmedLatterHalf.length) {
      return `${base.slice(0, Math.max(0, fromIndex + 1)).trim()}${ELLIPSIS}${trimmedLatterHalf}`;
    }

    return `${trimmedFirstHalf}${ELLIPSIS}${trimmedLatterHalf}`;
  }

  return base;
};

/**
 * Converts a decimal percent 0-1 to string percent 0-100%. Do not include decimals for 0.
 */
export const decimalPercentToString = (value: Decimal, toFixed = 0): string => {
  const percentage = value.times(100);

  return `${percentage.toDecimalPlaces(toFixed).toString()}%`;
};

export const joinStrings = (values: (null | number | string | undefined)[] = [], separator = ', '): string =>
  values.filter(Boolean).join(separator);

export const isURL = (text: string) => /[\w#%&+./:=?@~-]{2,256}\.[a-z]{2,4}\b(\/[\w#%&+./:=?@~-]*)?/gi.test(text);

export const isValidEmail = (value: string) =>
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/.test(
    value
  );

/**
 * Taken from https://stackoverflow.com/questions/4338267/validate-phone-number-with-javascript
 *
 * @param {string} text
 * @returns {boolean}
 */
export const isPhoneNumber = (text: string) => /^\+?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4,6}$/gi.test(text);

export const conditionalString = (show: boolean, string_: string, defaultString = ''): string =>
  show ? string_ : defaultString;

export const stripNonAscii = (string_: string): string => string_.replaceAll(/[^\u{20}-\u{7e}]/gu, '');

export const stripHTMLTags = (string_: string): string => string_.replaceAll(/(<([^>]+)>)/gi, '');

export const stripWhitespace = (value: string): string => value.replaceAll(/\s+/g, '');

export const convertPixelToNumber = (string_: string): number => Number(string_.replace('px', ''));
